import { createTripWorkspace, isTripWorkspace, type TripFilmMoment, type TripPlanItem, type TripWorkspace } from "@japangolmok/core";

import { editions, origins } from "@japangolmok/content";

export const MAX_TRIP_FILE_BYTES = 20 * 1024 * 1024;
export type TripExportOptions = { dates: boolean; amounts: boolean; photos: boolean; notes: boolean };
export const privateExportDefaults: TripExportOptions = { dates: false, amounts: false, photos: false, notes: false };
type TemplateMoment = Omit<TripFilmMoment, "dayDate" | "capturedAt" | "createdAt"> & { dayIndex: number };
type TemplateSettings = Pick<TripWorkspace, "title" | "origin" | "destination" | "destinationCity" | "partySize" | "styles" | "dailyBudgetJpy" | "krwPer100Jpy">;
export type TripTemplate = { settings: TemplateSettings; days: { items: TripPlanItem[] }[]; checklist: TripWorkspace["checklist"]; candidates: TripPlanItem[]; filmMoments: TemplateMoment[]; companionNotes: NonNullable<TripWorkspace["companionNotes"]> };
export type TripImportPreview = { kind: "trip"; trip: TripWorkspace } | { kind: "template"; template: TripTemplate; trip: TripWorkspace };
const supportedTrip = (trip: TripWorkspace) => Object.hasOwn(editions, trip.destination) && origins.some((origin) => origin.iata === trip.origin);
const fileBytes = (text: string) => new Blob([text]).size;
function checkSize(text: string) {
  if (text.length > MAX_TRIP_FILE_BYTES || fileBytes(text) > MAX_TRIP_FILE_BYTES) throw new Error("TRIP_FILE_TOO_LARGE");
}
const settings = (trip: TripWorkspace): TemplateSettings => ({ title: trip.title, origin: trip.origin, destination: trip.destination, destinationCity: trip.destinationCity, partySize: trip.partySize, styles: [...trip.styles], dailyBudgetJpy: trip.dailyBudgetJpy, krwPer100Jpy: trip.krwPer100Jpy });
const dayAt = (start: string, index: number) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) throw new Error("INVALID_TRIP_DATE");
  const date = new Date(`${start}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== start) throw new Error("INVALID_TRIP_DATE");
  date.setUTCDate(date.getUTCDate() + index);
  return date.toISOString().slice(0, 10);
};
function templateToTrip(template: TripTemplate, startDate: string, nowIso: string): TripWorkspace {
  if (!template || !Array.isArray(template.days) || template.days.length < 1 || template.days.length > 31 || !Array.isArray(template.checklist) || !Array.isArray(template.filmMoments) || !Array.isArray(template.candidates) || !Array.isArray(template.companionNotes)) throw new Error("INVALID_TRIP_WORKSPACE");
  const trip = createTripWorkspace({ ...template.settings, startDate, endDate: dayAt(startDate, template.days.length - 1) }, nowIso);
  trip.days = template.days.map((day, index) => ({ date: dayAt(startDate, index), items: day.items }));
  trip.checklist = template.checklist;
  trip.candidates = template.candidates;
  trip.companionNotes = template.companionNotes;
  trip.filmMoments = template.filmMoments.map(({ dayIndex, ...moment }) => {
    if (!Number.isInteger(dayIndex) || dayIndex < 0 || dayIndex >= trip.days.length) throw new Error("INVALID_TRIP_WORKSPACE");
    const dayDate = dayAt(startDate, dayIndex);
    return { ...moment, dayDate, capturedAt: `${dayDate}T12:00:00+09:00`, createdAt: nowIso };
  });
  if (!isTripWorkspace(trip) || !supportedTrip(trip)) throw new Error("INVALID_TRIP_WORKSPACE");
  return trip;
}

/** Build an allowlisted copy; unselected data never enters the downloaded file. */
export function selectTripForExport(trip: TripWorkspace, options: TripExportOptions): TripWorkspace {
  if (!isTripWorkspace(trip) || !supportedTrip(trip)) throw new Error("INVALID_TRIP_WORKSPACE");
  const item = (entry: TripPlanItem): TripPlanItem => ({
    id: entry.id, type: entry.type, title: entry.title, area: entry.area,
    time: options.dates ? entry.time : null, estimateJpy: options.amounts ? entry.estimateJpy : null,
    mapQuery: entry.mapQuery, note: options.notes ? entry.note : null,
    completed: options.dates ? entry.completed : false,
    placeId: entry.placeId, courseId: entry.courseId, role: entry.role, setting: entry.setting,
    japaneseName: entry.japaneseName, sourceUrl: entry.sourceUrl, checkedAt: entry.checkedAt,
    selectionReason: entry.selectionReason, stayMinutes: entry.stayMinutes,
    locked: options.dates ? entry.locked : false, deferred: entry.deferred,
    returnReason: options.notes ? entry.returnReason : undefined,
  });
  // Memo items are free text themselves, even if their note field is empty.
  const include = (entry: TripPlanItem) => options.notes || entry.type !== "memo";
  const days = trip.days.map((day) => ({ date: day.date, items: day.items.filter(include).map(item) }));
  return {
    schemaVersion: 1, id: trip.id, ...settings(trip),
    title: options.notes ? trip.title : `${trip.destinationCity} 골목 여행`,
    dailyBudgetJpy: options.amounts ? trip.dailyBudgetJpy : 0, krwPer100Jpy: options.amounts ? trip.krwPer100Jpy : null,
    startDate: trip.startDate, endDate: trip.endDate, stageOverride: options.dates ? trip.stageOverride : null, days,
    checklist: options.notes ? trip.checklist.map((entry) => ({ id: entry.id, title: entry.title, group: entry.group, done: options.dates ? entry.done : false, officialUrl: entry.officialUrl })) : [],
    expenses: options.amounts && options.dates && options.notes ? trip.expenses : [],
    fareNotes: options.amounts && options.dates && options.notes ? trip.fareNotes : [],
    candidates: (trip.candidates ?? []).filter(include).map(item),
    companionNotes: options.notes ? (trip.companionNotes ?? []) : [],
    filmMoments: (trip.filmMoments ?? []).filter((moment) => options.notes || !trip.days.find((day) => day.date === moment.dayDate)?.items.some((entry) => entry.id === moment.planItemId && entry.type === "memo")).map((moment) => ({
      id: moment.id, planItemId: days.find((day) => day.date === moment.dayDate)?.items.some((entry) => entry.id === moment.planItemId) ? moment.planItemId : null,
      dayDate: moment.dayDate, placeTitle: moment.placeTitle, neighborhood: moment.neighborhood,
      capturedAt: moment.capturedAt, createdAt: moment.createdAt, featured: moment.featured,
      photoDataUrl: options.photos ? moment.photoDataUrl : null, photoMimeType: options.photos ? moment.photoMimeType : null,
      note: options.notes ? moment.note : undefined,
    })),
    createdAt: trip.createdAt, updatedAt: trip.updatedAt,
  };
}

export function serializeTripSelection(trip: TripWorkspace, options: TripExportOptions = privateExportDefaults): string {
  const selected = selectTripForExport(trip, options);
  let envelope: unknown;
  if (options.dates) {
    envelope = { exportType: "japangolmok-trip", schemaVersion: 1, trip: selected };
  } else {
    // IDs and arbitrary source timestamps are not copied into a date-free template.
    const remapped = new Map<string, string>();
    const days = selected.days.map((day, dayIndex) => ({ items: day.items.map((entry, index) => {
      const id = `day-${dayIndex}-item-${index}`;
      remapped.set(`${day.date}:${entry.id}`, id);
      return { ...entry, id };
    }) }));
    const template: TripTemplate = {
      settings: settings(selected), days,
      checklist: selected.checklist.map((entry, index) => ({ ...entry, id: `check-${index}` })),
      candidates: (selected.candidates ?? []).map((entry, index) => ({ ...entry, id: `candidate-${index}` })),
      companionNotes: (selected.companionNotes ?? []).map((entry, index) => ({ ...entry, id: `companion-${index}` })),
      filmMoments: (selected.filmMoments ?? []).map((moment, index) => ({
        id: `scene-${index}`, planItemId: moment.planItemId ? remapped.get(`${moment.dayDate}:${moment.planItemId}`) ?? null : null,
        dayIndex: selected.days.findIndex((day) => day.date === moment.dayDate), placeTitle: moment.placeTitle, neighborhood: moment.neighborhood,
        photoDataUrl: moment.photoDataUrl, photoMimeType: moment.photoMimeType, featured: moment.featured, note: moment.note,
      })),
    };
    envelope = { exportType: "japangolmok-template", schemaVersion: 1, template };
  }
  const text = `${JSON.stringify(envelope, null, 2)}\n`;
  checkSize(text);
  return text;
}

export function previewTripImport(input: string): TripImportPreview {
  checkSize(input);
  let data: unknown;
  try { data = JSON.parse(input); } catch { throw new Error("INVALID_TRIP_JSON"); }
  if (!data || typeof data !== "object") throw new Error("INVALID_TRIP_WORKSPACE");
  const envelope = data as { exportType?: string; schemaVersion?: number; template?: TripTemplate; trip?: unknown };
  if (envelope.exportType === "japangolmok-template") {
    if (envelope.schemaVersion !== 1 || !envelope.template) throw new Error("INVALID_TRIP_WORKSPACE");
    const trip = templateToTrip(envelope.template, "2000-01-01", "2000-01-01T00:00:00Z");
    return { kind: "template", template: envelope.template, trip };
  }
  if (envelope.exportType !== undefined && envelope.exportType !== "japangolmok-trip") throw new Error("INVALID_TRIP_WORKSPACE");
  if (envelope.exportType && envelope.schemaVersion !== undefined && envelope.schemaVersion !== 1) throw new Error("INVALID_TRIP_WORKSPACE");
  const trip = "trip" in envelope ? envelope.trip : data;
  if (!isTripWorkspace(trip) || !supportedTrip(trip)) throw new Error("INVALID_TRIP_WORKSPACE");
  return { kind: "trip", trip };
}

export function confirmTripImport(preview: TripImportPreview, startDate?: string, nowIso = new Date().toISOString()): TripWorkspace {
  const trip = preview.kind === "template"
    ? templateToTrip(preview.template, startDate ?? "", nowIso)
    : structuredClone(preview.trip);
  const imported = { ...trip, id: crypto.randomUUID(), title: `${trip.title} · 가져온 여행`.slice(0, 80), stageOverride: null, lastAdjustment: null, lastReplacement: null, createdAt: nowIso, updatedAt: nowIso };
  if (!isTripWorkspace(imported)) throw new Error("INVALID_TRIP_WORKSPACE");
  return imported;
}
